import { useEffect, useMemo, useState } from 'react';
import { ContentTitle } from '../../components/typography/Heading';
import Reward from './Reward';
import {
    isRewardCreateParticipant,
    isRewardLottery,
    isRewardProductReward,
    isRewardSoldout,
    mapProductData,
} from './reward-model';
import { useProject } from '../project-hooks/useProject';
import ScreenReaderOnly from '../../accessibility/ScreenReaderOnly';
import { ProductCategoryModal } from '../../components/product-category-modal/ProductCategoryModal';
import { trackEvent } from '../../support-and-tracking/analytics-actions';
import { fetchCMSContentbyId } from '../../cms/cms-actions';
import { useDispatch, useSelector } from 'react-redux';
import { getProductCategoriesForProject } from '../project-actions';
import { getProductRewardCategoriesByProjectId } from '../projects-reducer';
import { useFeature } from '../../components/hooks/isFeatureEnabled';
import { USE_READ_MORE_ON_REWARD } from '../../constants/features/constants';
import { RewardListSection } from './RewardListSection';
import { openModal } from '../product-category-modal/product-category-modal-actions';
import styles from './reward-list.module.less';
import { useNavigate } from 'react-router-dom';
import { useSubproject } from '../../components/hooks/useSubproject';
import volunteer_activism from '@sb1/ffe-icons/icons/open/300/md/volunteer_activism.svg';
import add_shopping_cart from '@sb1/ffe-icons/icons/open/300/md/add_shopping_cart.svg';

const RewardList = ({
    rewards = [],
    className = '',
    showSupportButton = true,
    showTitle = true,
    onProjectPage = true,
    titleClass = '',
    descriptionClass = '',
    showCreateParticipantRewards = true,
}) => {
    const project = useProject();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { subproject } = useSubproject();
    const hideRewardsPurchasedCount = project?.profile?.hideRewardsPurchasedCount;
    const hideChangeCountButtons = project?.profile?.hideChangeCountButtons;
    const hideSupportButton = project?.profile?.hideSupportButton;

    const single = useMemo(() => rewards?.length === 1, [rewards?.length]);
    const [products, setProducts] = useState({});
    const productCategoriesForProject = useSelector((state) =>
        getProductRewardCategoriesByProjectId(state, project?.id)
    );
    const useReadMoreOnReward = useFeature(USE_READ_MORE_ON_REWARD);

    useEffect(() => {
        if (rewards?.filter((r) => r.product_reward_id)?.length > 0 && useReadMoreOnReward) {
            dispatch(getProductCategoriesForProject({ projectId: project.id }));
        }
    }, [project?.id, rewards, useReadMoreOnReward]);

    useEffect(() => {
        const fetchData = async () => {
            const productCategoriesWithContentfulData = productCategoriesForProject?.filter(
                (category) => category.contentful_data
            );
            if (productCategoriesWithContentfulData?.length > 0) {
                const contentfulDataToFetch = productCategoriesWithContentfulData.map(
                    (category) => category.contentful_data
                );
                const requests = contentfulDataToFetch.map((e) =>
                    dispatch(fetchCMSContentbyId({ resourceId: e, depth: 3 }))
                );
                const results = await Promise.all(requests);
                const { productsObject } = mapProductData({
                    productInformationFromContentful: results.map((r) => r.data),
                    productCategories: rewards.map((r) => ({ price: r.price, id: r.product_reward_category_id })),
                });
                setProducts(productsObject);
            }
        };

        if (useReadMoreOnReward) {
            fetchData().catch(() => {});
        }
    }, [productCategoriesForProject, rewards, useReadMoreOnReward]);

    if (!project || !rewards || rewards.length === 0) {
        return null;
    }

    const productRewardsIncludingLottery = rewards.filter(
        (reward) => isRewardLottery(reward) || isRewardProductReward(reward)
    );
    const productRewardsExcludingLottery = productRewardsIncludingLottery.filter((reward) => !isRewardLottery(reward));
    const allRewardsAreLotteryTickets = productRewardsIncludingLottery.every(isRewardLottery);
    const createParticipantRewards = rewards
        .filter(isRewardCreateParticipant)
        .filter((reward) => !(project.profile?.hideSoldoutRewards && isRewardSoldout(reward)));
    const regularRewards = rewards.filter((reward) => {
        if (project.profile?.hideSoldoutRewards && isRewardSoldout(reward)) {
            return false;
        }
        return !isRewardLottery(reward) && !isRewardProductReward(reward) && !isRewardCreateParticipant(reward);
    });

    const lotteryTicket = rewards.find(isRewardLottery);
    const singleOrNoItemInEachList =
        productRewardsIncludingLottery?.length <= 1 &&
        regularRewards?.length <= 1 &&
        createParticipantRewards?.length <= 1;

    const showRewardsOnTop = project.profile?.showRewardsOnTop;
    const defaultRewardTitle = {
        title: project.profile?.defaultRewardTitle ?? 'Gi det du vil',
        description: project.profile?.defaultRewardDescription ?? 'For deg som vil støtte med et valgfritt beløp',
    };

    const onModalOpen = (productId, rewardSupportConfig) => {
        dispatch(openModal({ product: products[productId], buyButtonConfig: rewardSupportConfig }));
        trackEvent('project-rewards', 'open-modal', {
            'product-id': productId,
        });
    };

    function getSupportButtonText(reward) {
        if (reward.profile?.navigateOnClick && project.profile?.rewardHeadline) {
            return 'Støtt';
        }
        return undefined;
    }

    return (
        <div className={[className, styles.rewardsContainer, showRewardsOnTop ? styles.reversed : ''].join(' ')}>
            <ScreenReaderOnly>
                <ContentTitle>Støtt spleisen</ContentTitle>
            </ScreenReaderOnly>
            {!hideSupportButton && showSupportButton && (
                <RewardListSection
                    singleView={singleOrNoItemInEachList}
                    onProjectPage={onProjectPage}
                    title={'Gi et valgfritt beløp'}
                    showTitle={showTitle}
                    titleClass={titleClass}
                    descriptionClass={descriptionClass}
                >
                    <Reward
                        reward={defaultRewardTitle}
                        canSelectMultiple={false}
                        supportButtonText="Støtt"
                        buttonIcon={volunteer_activism}
                        hideDefaultImage
                        onClick={() =>
                            navigate({
                                pathname: `/project/${project.id}/payment`,
                                search: subproject ? `?subproject=${subproject.id}` : '',
                            })
                        }
                    />
                </RewardListSection>
            )}
            {createParticipantRewards?.length > 0 && showCreateParticipantRewards && (
                <RewardListSection
                    onProjectPage={onProjectPage}
                    title={`Meld deg på!`}
                    showTitle={showTitle}
                    singleView={singleOrNoItemInEachList}
                    titleClass={titleClass}
                    description={
                        project.profile?.createParticipantRewardDescription ||
                        'Meld deg på og få opprettet en deltagerside'
                    }
                    showDescription={
                        project.profile?.createParticipantRewardDescription || !project.profile?.rewardHeadline
                    }
                    descriptionClass={descriptionClass}
                >
                    {createParticipantRewards.map((reward) => (
                        <Reward
                            reward={reward}
                            key={reward?.id}
                            navigateOnClick={single || reward.profile?.navigateOnClick}
                            canSelectMultiple={!hideChangeCountButtons && !reward.profile?.navigateOnClick}
                            supportButtonText={getSupportButtonText(reward)}
                            hidePurchasedCount={hideRewardsPurchasedCount}
                            buttonIcon={add_shopping_cart}
                        />
                    ))}
                </RewardListSection>
            )}
            {regularRewards?.length > 0 && (
                <RewardListSection
                    onProjectPage={onProjectPage}
                    title={project.profile?.rewardHeadline || `Kjøp belønninger`}
                    showTitle={showTitle}
                    singleView={singleOrNoItemInEachList}
                    titleClass={titleClass}
                    description={
                        project.profile?.rewardDescription || 'Innsamler har ansvar for distribusjon av belønninger'
                    }
                    showDescription={project.profile?.rewardDescription || !project.profile?.rewardHeadline}
                    descriptionClass={descriptionClass}
                >
                    {regularRewards.map((reward) => (
                        <Reward
                            reward={reward}
                            key={reward?.id}
                            navigateOnClick={single || reward.profile?.navigateOnClick}
                            canSelectMultiple={!hideChangeCountButtons && !reward.profile?.navigateOnClick}
                            supportButtonText={getSupportButtonText(reward)}
                            hidePurchasedCount={hideRewardsPurchasedCount}
                            buttonIcon={add_shopping_cart}
                        />
                    ))}
                </RewardListSection>
            )}
            {productRewardsIncludingLottery?.length > 0 && (
                <RewardListSection
                    onProjectPage={onProjectPage}
                    title={allRewardsAreLotteryTickets ? 'Kjøp lodd' : `Kjøp produkter`}
                    showTitle={showTitle}
                    singleView={singleOrNoItemInEachList}
                    titleClass={titleClass}
                    description={
                        'Spleis sender alle produkter direkte hjem til deg. Forventet leveringstid er 5 - 12 dager.'
                    }
                    descriptionClass={descriptionClass}
                >
                    {lotteryTicket && (
                        <Reward
                            reward={lotteryTicket}
                            purchasedText={'lodd kjøpt'}
                            defaultValue={4}
                            navigateOnClick={single}
                            onModalOpen={onModalOpen}
                            hasModalInfo={!!products[lotteryTicket.product_reward_category_id]}
                            buttonIcon={add_shopping_cart}
                        />
                    )}
                    {productRewardsExcludingLottery.map((reward) => (
                        <Reward
                            reward={reward}
                            key={reward?.id}
                            navigateOnClick={single}
                            onModalOpen={onModalOpen}
                            hasModalInfo={!!products[reward.product_reward_category_id]}
                            buttonIcon={add_shopping_cart}
                            hideStock={true}
                        />
                    ))}
                </RewardListSection>
            )}
            <ProductCategoryModal />
        </div>
    );
};

export default RewardList;
